// Media control — docs/06 §3: mute (track.enabled=false, keeps encryption &
// timing), speaker routing, mic device selection, and the iOS Safari gesture
// rule (audio output must start from a user gesture — the accept tap calls
// `unlockAudio()`). Platform APIs are injected; browsers get them from
// `browserMediaDeps()` at wiring time (J9).

import { Store, type ReadableStore } from '../store.js';
import type { AudioTrackLike, MediaStreamLike } from '../rtc/peer.js';

export interface MediaDeviceInfoLike {
  deviceId: string;
  kind: string;
  label: string;
}

/** Playback surface for remote audio (an <audio> element in the browser). */
export interface AudioSinkLike {
  play(): Promise<void>;
  pause?(): void;
  /** Chrome/Edge output routing; absent on iOS (route follows the OS). */
  setSinkId?(deviceId: string): Promise<void>;
  srcObject?: unknown;
}

export interface MediaControlDeps {
  getUserMedia(constraints: { audio: Record<string, unknown> }): Promise<MediaStreamLike>;
  enumerateDevices?(): Promise<MediaDeviceInfoLike[]>;
  /** Resume the platform audio context; only succeeds inside a user gesture. */
  resumeAudioContext?(): Promise<void>;
  sink?: AudioSinkLike;
}

export interface MediaControlState {
  muted: boolean;
  speaker: boolean;
  micDeviceId: string | null;
  mics: MediaDeviceInfoLike[];
  outputs: MediaDeviceInfoLike[];
  /** iOS: output is locked until a user gesture unlocks the audio session. */
  audioUnlocked: boolean;
}

const INITIAL: MediaControlState = {
  muted: false,
  speaker: false,
  micDeviceId: null,
  mics: [],
  outputs: [],
  audioUnlocked: false,
};

/** Standard hygiene constraints (docs/01 §4.2); always on. */
export const MIC_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
} as const;

export class MediaControl {
  private readonly store = new Store<MediaControlState>(INITIAL);
  private stream: MediaStreamLike | null = null;

  constructor(private readonly deps: MediaControlDeps) {}

  get state(): ReadableStore<MediaControlState> {
    return this.store;
  }

  get currentStream(): MediaStreamLike | null {
    return this.stream;
  }

  /** Acquire (or re-acquire) the mic, optionally pinned to a device. */
  async acquireMic(deviceId?: string): Promise<MediaStreamLike> {
    this.releaseMic();
    const audio: Record<string, unknown> = { ...MIC_CONSTRAINTS };
    if (deviceId) audio['deviceId'] = { exact: deviceId };
    const stream = await this.deps.getUserMedia({ audio });
    this.stream = stream;
    this.applyMute(this.store.get().muted);
    this.store.patch({ micDeviceId: deviceId ?? null });
    return stream;
  }

  releaseMic(): void {
    for (const track of this.stream?.getAudioTracks() ?? []) track.stop?.();
    this.stream = null;
  }

  /** Mute keeps sending silence frames — never remove/stop the track mid-call. */
  setMuted(muted: boolean): void {
    this.applyMute(muted);
    this.store.patch({ muted });
  }

  /**
   * Speaker toggle. Where `setSinkId` exists we route to the chosen output;
   * elsewhere (iOS) the flag is published for the native/UI layer to act on.
   */
  async setSpeaker(on: boolean, outputDeviceId?: string): Promise<void> {
    this.store.patch({ speaker: on });
    const sink = this.deps.sink;
    if (sink?.setSinkId && outputDeviceId !== undefined) {
      await sink.setSinkId(outputDeviceId).catch(() => {
        // routing best-effort; the call must not fail over a sink error
      });
    }
  }

  /**
   * iOS gesture rule: must be invoked from a user-gesture handler (the accept
   * tap) so the audio session may start playing (docs/06 §3).
   */
  async unlockAudio(): Promise<void> {
    await this.deps.resumeAudioContext?.().catch(() => {
      // will retry on the next gesture
    });
    await this.deps.sink?.play().catch(() => {
      // same: locked until a real gesture
    });
    this.store.patch({ audioUnlocked: true });
  }

  async refreshDevices(): Promise<void> {
    if (!this.deps.enumerateDevices) return;
    const devices = await this.deps.enumerateDevices();
    this.store.patch({
      mics: devices.filter((d) => d.kind === 'audioinput'),
      outputs: devices.filter((d) => d.kind === 'audiooutput'),
    });
  }

  private applyMute(muted: boolean): void {
    for (const track of this.stream?.getAudioTracks() ?? []) track.enabled = !muted;
  }
}

export type { AudioTrackLike, MediaStreamLike };
